import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import { AuthService } from '@auth0/auth0-angular';
import { BehaviorSubject, Observable, Subject, catchError, map, of, shareReplay, switchMap, takeUntil, tap } from 'rxjs';

import { environment } from '../../../../../environments/environment';
import { Permission } from '../models/permission.model';

/** Resposta de GET /api/v1/me/permissions (envelope PharmaManagerResponse). */
interface MePermissionsResponse {
  success: boolean;
  data?: {
    role?: string | null;
    admin?: boolean;
    permissions?: Permission[];
  };
}

/**
 * Autorização por tela da área de gestão. As permissões vêm do backend
 * (`/me/permissions`, resolvidas pelo auth-manager por e-mail), não das claims do token:
 * o tenant do Otimiza não injeta `manager_permissions` como o do Manager fazia. Convive
 * com o `PermissionService` do Otimiza, que decide por grupo — camadas em série (AD-02).
 *
 * ponytail: ainda fail-open (permissão não encontrada ⇒ libera), herdado do Manager.
 * A inversão para fail-closed é a T041; junto vem trocar a fonte para o `/permissions`
 * (traz a flag `admin` do banco), senão um admin "puro" — sem páginas no perfil — seria
 * trancado. Hoje o fail-open cobre esse admin por acaso.
 */
@Injectable({ providedIn: 'root' })
export class ManagerPermissionService implements OnDestroy {
  private readonly permissionsSubject = new BehaviorSubject<Permission[] | null>(null);
  private readonly userRoleSubject = new BehaviorSubject<string | null>(null);
  private readonly destroy$ = new Subject<void>();

  private permissionsLoaded = false;
  /** Segura chamadas concorrentes (guard + itens de menu) numa requisição só. */
  private inFlight$: Observable<Permission[] | null> | null = null;

  constructor(private http: HttpClient, private auth0: AuthService) {
    this.watchAuthentication();
  }

  ensureRoleLoaded(): Observable<string | null> {
    return this.ensurePermissionsLoaded().pipe(
      map(() => this.userRoleSubject.getValue())
    );
  }

  ensurePermissionsLoaded(): Observable<Permission[] | null> {
    if (this.permissionsLoaded) {
      return of(this.permissionsSubject.getValue());
    }
    if (this.inFlight$) {
      return this.inFlight$;
    }

    this.inFlight$ = this.auth0.isAuthenticated$.pipe(
      switchMap(isAuthenticated => (isAuthenticated ? this.fetchPermissions() : of(null))),
      tap(() => this.inFlight$ = null),
      shareReplay(1)
    );
    return this.inFlight$;
  }

  getPermissions(): Permission[] | null {
    return this.permissionsSubject.getValue();
  }

  getUserRole(): string | null {
    return this.userRoleSubject.getValue();
  }

  isAdminRole(): boolean {
    // ponytail: sem permissões o usuário vira admin (fail-open). Cobre o admin "puro"
    // hoje; sai na T041 junto com a troca para o /permissions.
    if (this.getPermissions()?.length === 0) return true;
    const role = this.getUserRole();
    return !!role && role.toLowerCase() === 'administrador';
  }

  hasPagePermission(target: string, action: 'read' | 'write' = 'read'): boolean {
    if (this.isAdminRole()) {
      return true;
    }

    const pagePerm = this.findPagePermission(this.getPermissions(), target);
    if (!pagePerm) {
      return true; // ponytail: fail-open — sai na T041.
    }

    return pagePerm[`can_${action}`] === true;
  }

  hasPagePermissionAsync(target: string, action: 'read' | 'write' = 'read'): Observable<boolean> {
    return this.ensurePermissionsLoaded().pipe(
      map(() => {
        if (this.isAdminRole()) {
          return true;
        }
        const pagePerm = this.findPagePermission(this.getPermissions(), target);
        if (!pagePerm) {
          return true; // ponytail: fail-open — sai na T041.
        }
        return pagePerm[`can_${action}`] === true;
      })
    );
  }

  clearPermissions(): void {
    this.permissionsSubject.next(null);
    this.userRoleSubject.next(null);
    this.permissionsLoaded = false;
    this.inFlight$ = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private fetchPermissions(): Observable<Permission[] | null> {
    return this.http.get<MePermissionsResponse>(`${environment.apiURLManager}/me/permissions`).pipe(
      map(response => {
        const permissions = response.data?.permissions ?? [];
        this.userRoleSubject.next(response.data?.role ?? null);
        this.permissionsSubject.next(permissions);
        this.permissionsLoaded = true;
        return permissions;
      }),
      catchError(error => {
        console.error('Erro ao carregar permissões de gestão:', error);
        // Sem resposta cai no fail-open (lista vazia ⇒ isAdminRole libera). Sai na T041.
        this.userRoleSubject.next(null);
        this.permissionsSubject.next([]);
        this.permissionsLoaded = true;
        return of([] as Permission[]);
      })
    );
  }

  /** Uma lista vazia é indistinguível de "não encontrei" — ambas caem no fail-open. */
  private findPagePermission(permissions: Permission[] | null, target: string): Permission | undefined {
    if (!permissions?.length) {
      return undefined;
    }
    return permissions.find(perm => perm.permission_level === 'PAGE' && perm.target === target);
  }

  /** Logout precisa zerar o cache; sem isso o próximo usuário herda as permissões do anterior. */
  private watchAuthentication(): void {
    this.auth0.isAuthenticated$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(isAuthenticated => {
      if (!isAuthenticated) {
        this.clearPermissions();
      }
    });
  }
}
